import React, { useState, useCallback, useEffect } from 'react'
import { ResumeItem } from '../../services/apiService'
import { ResumeFitAnalysisResponse, TailoredResumeResponse } from '../../services/jobApplicationService'
import './Step2ResumeSelection.css'

interface Step2ResumeSelectionProps {
  fitAnalysis: ResumeFitAnalysisResponse | null
  availableResumes: ResumeItem[]
  tailoredResume: TailoredResumeResponse | null
  selectedResumeId: string | null
  selectionType: 'best-fit' | 'saved' | 'custom'
  bestFitResumeId: string | null
  onResumeSelect: (resumeId: string | null, type: 'best-fit' | 'saved' | 'custom') => void
  onNext: () => void
  onBack: () => void
}

export const Step2ResumeSelection: React.FC<Step2ResumeSelectionProps> = ({
  fitAnalysis,
  availableResumes,
  tailoredResume,
  selectedResumeId,
  selectionType,
  bestFitResumeId,
  onResumeSelect,
  onNext,
  onBack,
}) => {
  const [localSelectionType, setLocalSelectionType] = useState<'best-fit' | 'saved' | 'custom'>(selectionType)
  const [localSelectedResumeId, setLocalSelectedResumeId] = useState<string | null>(selectedResumeId)

  useEffect(() => {
    setLocalSelectionType(selectionType)
    setLocalSelectedResumeId(selectedResumeId)
  }, [selectionType, selectedResumeId])

  const bestFitAnalysis = fitAnalysis?.resumeFitAnalyses && fitAnalysis.resumeFitAnalyses.length > 0
    ? [...fitAnalysis.resumeFitAnalyses].sort((a, b) => b.fitScore - a.fitScore)[0]
    : null
  const bestFitResume = bestFitResumeId
    ? availableResumes.find(r => r.id === bestFitResumeId)
    : null
  const bestFitScore = bestFitAnalysis?.fitScore || null

  const getFitScoreColor = (score: number): string => {
    if (score >= 80) return 'excellent'
    if (score >= 60) return 'good'
    if (score >= 40) return 'moderate'
    return 'low'
  }

  const handleSelectionChange = useCallback((type: 'best-fit' | 'saved' | 'custom', resumeId: string | null = null) => {
    setLocalSelectionType(type)
    const finalResumeId = resumeId || (type === 'best-fit' ? bestFitResumeId : null)
    setLocalSelectedResumeId(finalResumeId)
    onResumeSelect(finalResumeId, type)
  }, [bestFitResumeId, onResumeSelect])

  const handleDropdownChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value || null
    setLocalSelectedResumeId(selectedId)
    
    // If tailored resume is selected, switch to custom type
    if (selectedId?.startsWith('tailored-')) {
      handleSelectionChange('custom', selectedId)
    } else {
      handleSelectionChange('saved', selectedId)
    }
  }, [handleSelectionChange])

  const canProceed = () => {
    if (localSelectionType === 'saved' && !localSelectedResumeId) return false
    if (localSelectionType === 'best-fit' && !bestFitResume) return false
    if (localSelectionType === 'custom' && !tailoredResume && !localSelectedResumeId?.startsWith('tailored-')) return false
    return true
  }

  const handleNext = useCallback(() => {
    if (canProceed()) {
      // If custom is selected, go to step 3, otherwise go to step 4
      if (localSelectionType === 'custom') {
        onNext()
      } else {
        // Skip step 3 (custom resume) and go to step 4 (confirmation)
        // We'll handle this in the parent component by checking selection type
        onNext()
      }
    }
  }, [localSelectionType, canProceed, onNext])

  return (
    <div className="step2-resume-selection">
      <div className="step-header">
        <h3>Select Resume</h3>
        <p className="step-description">
          Choose which resume to use for this job application. You can select the best fit, 
          choose from your saved resumes, or use a custom tailored resume.
        </p>
      </div>

      <div className="step2-selection-options">
        {/* Best Fit Option */}
        <div 
          className={`step2-option-card ${localSelectionType === 'best-fit' ? 'selected' : ''} ${!bestFitResume ? 'disabled' : ''}`}
          onClick={() => bestFitResume && handleSelectionChange('best-fit')}
        >
          <div className="step2-option-header">
            <input
              type="radio"
              name="resume-selection"
              value="best-fit"
              checked={localSelectionType === 'best-fit'}
              onChange={() => bestFitResume && handleSelectionChange('best-fit')}
              disabled={!bestFitResume}
            />
            <div className="step2-option-content">
              <span className="step2-option-label">Use Best Fit Resume</span>
              {bestFitResume && bestFitScore !== null && (
                <div className={`step2-fit-badge ${getFitScoreColor(bestFitScore)}`}>
                  {Math.round(bestFitScore)}% Match
                </div>
              )}
            </div>
          </div>
          {bestFitResume && (
            <div className="step2-option-details">
              <p className="step2-resume-name">{bestFitResume.originalFilename || bestFitResume.filename || 'Best Fit Resume'}</p>
              <p className="step2-option-description">
                This resume has the highest match score with the job requirements.
              </p>
            </div>
          )}
          {!bestFitResume && (
            <p className="step2-option-description">No best fit resume available</p>
          )}
        </div>

        {/* Saved Resume Option */}
        <div 
          className={`step2-option-card ${localSelectionType === 'saved' ? 'selected' : ''}`}
          onClick={() => handleSelectionChange('saved')}
        >
          <div className="step2-option-header">
            <input
              type="radio"
              name="resume-selection"
              value="saved"
              checked={localSelectionType === 'saved'}
              onChange={() => handleSelectionChange('saved')}
            />
            <div className="step2-option-content">
              <span className="step2-option-label">Select Different Resume</span>
            </div>
          </div>
          {localSelectionType === 'saved' && (
            <div className="step2-dropdown-container">
              <select
                className="step2-resume-dropdown"
                value={localSelectedResumeId || ''}
                onChange={handleDropdownChange}
              >
                <option value="">-- Select a resume --</option>
                {availableResumes.map(resume => (
                  <option key={resume.id} value={resume.id}>
                    {resume.originalFilename || resume.filename || `Resume ${resume.id}`}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Custom Resume Option */}
        {(tailoredResume || localSelectedResumeId?.startsWith('tailored-')) && (
          <div 
            className={`step2-option-card ${localSelectionType === 'custom' ? 'selected' : ''}`}
            onClick={() => handleSelectionChange('custom', localSelectedResumeId?.startsWith('tailored-') ? localSelectedResumeId : null)}
          >
            <div className="step2-option-header">
              <input
                type="radio"
                name="resume-selection"
                value="custom"
                checked={localSelectionType === 'custom'}
                onChange={() => handleSelectionChange('custom', localSelectedResumeId?.startsWith('tailored-') ? localSelectedResumeId : null)}
              />
              <div className="step2-option-content">
                <span className="step2-option-label">Use Custom Tailored Resume</span>
              </div>
            </div>
            <div className="step2-option-details">
              <p className="step2-option-description">
                {tailoredResume 
                  ? 'Use the custom tailored resume generated for this job application.'
                  : 'A custom tailored resume is available for this application.'}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="step-actions">
        <button className="btn btn-secondary" onClick={onBack}>
          ← Back
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleNext}
          disabled={!canProceed()}
        >
          Continue →
        </button>
      </div>
    </div>
  )
}

